import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useToast } from '@/contexts/ToastContext'
import {
  Bookmark,
  Filter,
  Search,
  Heart,
  Trash2,
  ExternalLink,
  Calendar,
  MapPin,
  Activity,
  Eye,
  Tag,
  Star,
  MoreVertical,
  Download,
  Grid,
  List,
  Image,
  Folder,
  ChevronLeft,
  ChevronRight,
  Move,
  Clock,
  StickyNote
} from 'lucide-react'
import { savedAdsApi } from '@/services/api'
import type { SavedAd, AdData, Folder as FolderType } from '@/types/shared'
import { FolderProvider, useFolders } from '@/contexts/FolderContext'
import FolderTree from '@/components/folders/FolderTree'
import { FolderSelectorModal } from '@/components/folders/FolderSelectorModal'
import CapturedAdCard from '@/components/captured/CapturedAdCard'


export function SavedAdsPage() {
  return (
    <FolderProvider>
      <SavedAdsPageContent />
    </FolderProvider>
  )
}

function SavedAdsPageContent() {
  const { showSuccess, showError } = useToast()
  const queryClient = useQueryClient()
  const { selectedFolder, selectFolder, getFolderPath } = useFolders()
  
  const [filters, setFilters] = useState({
    folderId: null as string | null,
    sortBy: 'savedAt'
  })
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedTexts, setExpandedTexts] = useState<Set<string>>(new Set())
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [adToMove, setAdToMove] = useState<{ id: string; currentFolderId?: string | null } | null>(null)

  // Get saved ads
  const { data, isLoading, error } = useQuery({
    queryKey: ['savedAds', filters],
    queryFn: () => savedAdsApi.getSavedAds(filters),
    onError: () => showError('Error al cargar anuncios guardados'),
    refetchOnWindowFocus: true, // Refetch when tab gains focus
    refetchOnMount: true, // Always refetch on mount
    staleTime: 0 // Consider data stale immediately
  })

  // Delete ad mutation
  const deleteMutation = useMutation(
    (id: string) => savedAdsApi.deleteSavedAd(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('savedAds')
        showSuccess('Anuncio eliminado exitosamente')
      },
      onError: (error: any) => {
        showError(error.response?.data?.message || 'Error al eliminar anuncio')
      }
    }
  )

  // Update favorite mutation
  const updateFavoriteMutation = useMutation(
    ({ id, isFavorite }: { id: string; isFavorite: boolean }) =>
      savedAdsApi.updateSavedAd(id, { isFavorite }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries('savedAds')
        showSuccess(variables.isFavorite ? 'Agregado a favoritos' : 'Removido de favoritos')
      },
      onError: (error: any) => {
        showError(error.response?.data?.message || 'Error al actualizar favorito')
      }
    }
  )

  // Move ad to folder mutation
  const moveAdMutation = useMutation(
    ({ id, folderId }: { id: string; folderId: string | null }) =>
      savedAdsApi.moveSavedAd(id, { folderId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('savedAds')
        showSuccess('Anuncio movido exitosamente')
      },
      onError: (error: any) => {
        showError(error.response?.data?.message || 'Error al mover el anuncio')
      }
    }
  )

  // Sync filters with selected folder
  React.useEffect(() => {
    if (selectedFolder) {
      setFilters(prev => ({ ...prev, folderId: selectedFolder._id }))
    } else {
      setFilters(prev => ({ ...prev, folderId: null }))
    }
  }, [selectedFolder])

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const handleDelete = (id: string, pageName: string) => {
    if (confirm(`¿Eliminar "${pageName}" de tus anuncios guardados?`)) {
      deleteMutation.mutate(id)
    }
  }

  const handleToggleFavorite = (id: string, isFavorite: boolean) => {
    updateFavoriteMutation.mutate({ id, isFavorite: !isFavorite })
  }

  const handleMoveToFolder = (id: string, currentFolderId?: string | null) => {
    setAdToMove({ id, currentFolderId })
    setShowMoveModal(true)
  }

  const handleFolderSelect = (folder: FolderType | null) => {
    if (adToMove) {
      moveAdMutation.mutate({ id: adToMove.id, folderId: folder?._id || null })
      setAdToMove(null)
    }
  }





  // Filter ads by search term
  const filteredAds = data?.ads?.filter((savedAd: SavedAd) => {
    if (!searchTerm) return true
    const ad = savedAd.adData
    return ad.page_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           ad.ad_creative_bodies?.[0]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           ad.ad_snapshot_url?.toLowerCase().includes(searchTerm.toLowerCase())
  }) || []

  if (error) {
    return (
      <div className="space-y-8">
        <div className="holographic-panel p-8 text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-red-400 mb-4">Error al cargar anuncios</h2>
          <p className="text-gray-300">No se pudieron cargar los anuncios guardados. Por favor, intenta de nuevo.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-stone-950">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-80' : 'w-0'} transition-all duration-300 bg-stone-900 border-r border-stone-800 overflow-hidden shadow-gum`}>
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-stone-800">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-stone-100 flex items-center gap-2">
                <Folder className="w-5 h-5 text-blue-400" />
                Organización
              </h2>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 hover:bg-stone-800 rounded-lg text-stone-300"
              >
                <ChevronLeft className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            {/* Current Folder Indicator */}
            <div className="mt-3 p-2 bg-stone-800/50 rounded-xl border border-stone-700">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-300">
                  Carpeta actual:
                </span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <Folder className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-stone-200">
                  {selectedFolder ? selectedFolder.name : 'TODOS'}
                </span>
              </div>
            </div>
          </div>

          {/* Folder Tree */}
          <div className="flex-1 overflow-y-auto">
            <FolderTree
              onFolderSelect={(folder) => {
                selectFolder(folder)
                setFilters(prev => ({
                  ...prev,
                  folderId: folder?._id || null
                }))
              }}
              selectedFolderId={selectedFolder?._id || null}
              showActions={true}
              className="h-full"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="bg-stone-900 border-b border-stone-800 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {!sidebarOpen && (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 hover:bg-stone-800 rounded-lg text-stone-300"
                >
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              )}
              
              {/* Breadcrumbs */}
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Bookmark className="w-4 h-4 text-blue-400" />
                <span>Anuncios Guardados</span>
                {selectedFolder && (
                  <>
                    <span>/</span>
                    <span className="text-blue-400">{selectedFolder.name}</span>
                  </>
                )}
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-stone-100 text-stone-900' : 'bg-stone-800 text-stone-300 hover:bg-stone-700'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-stone-100 text-stone-900' : 'bg-stone-800 text-stone-300 hover:bg-stone-700'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-stone-900/80 border-b border-stone-800 px-6 py-4">
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar en anuncios guardados..."
                className="w-full pl-10 pr-4 py-2 bg-stone-900 border border-stone-600 rounded-xl focus:ring-2 focus:ring-secondary-500/30 focus:border-secondary-400 text-stone-100 placeholder-stone-500"
              />
            </div>

            {/* Sort Options */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-300 whitespace-nowrap">
                Ordenar:
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="px-3 py-2 bg-stone-900 border border-stone-600 rounded-xl text-stone-100 focus:ring-2 focus:ring-secondary-500/30"
              >
                <option value="savedAt">Fecha guardado</option>
                <option value="lastViewed">Última vista</option>
                <option value="hotnessScore">Popularidad</option>
                <option value="daysRunning">Días ejecutándose</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1920px] mx-auto">

            {/* Loading State */}
            {isLoading && (
              <div className="holographic-panel p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
                <p className="text-gray-300">Cargando anuncios guardados...</p>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && (!data?.ads || data.ads.length === 0) && (
              <div className="holographic-panel p-8 text-center">
                <Bookmark className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-300 mb-2">
                  {selectedFolder ? `No hay anuncios en "${selectedFolder.name}"` : 'No hay anuncios guardados'}
                </h3>
                <p className="text-gray-400">
                  {selectedFolder 
                    ? 'Esta carpeta está vacía. Guarda algunos anuncios aquí para organizarlos.'
                    : 'Guarda anuncios desde la página de búsqueda para verlos aquí.'
                  }
                </p>
              </div>
            )}

            {/* Ads Grid/List */}
            {!isLoading && data?.ads && data.ads.length > 0 && (
              // La MISMA tarjeta que Capturas (CapturedAdCard), no una parecida. Si el guardado
              // vino de Capturas, el servidor manda su captura en `capture` y se ve todo:
              // evolución, anunciante, nicho y la oferta. Si se guardó desde la búsqueda, se
              // pinta con lo poco que el guardado conserva.
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {filteredAds.map((savedAd: SavedAd) => {
                  const ad = savedAd.adData as any
                  const a = savedAd.analysis || ({} as SavedAd['analysis'])
                  const item = savedAd.capture
                    ? { ...savedAd.capture, adData: ad }
                    : {
                        _id: savedAd._id,
                        pageName: ad.page_name,
                        adData: ad,
                        status: a.capturedStatus,
                        triggers: a.capturedTriggers,
                        firstCapturedAt: a.firstCapturedAt || savedAd.savedAt,
                        metrics: {
                          currentCollationCount: ad.collation_count ?? a.hotnessScore ?? 0,
                          scalingVelocity: a.scalingVelocity ?? 0,
                          currentDaysRunning: a.daysRunning ?? ad.days_running ?? 0
                        }
                      }
                  const carpeta = savedAd.folderId ? getFolderPath(savedAd.folderId) : ''

                  return (
                    <CapturedAdCard
                      key={savedAd._id}
                      item={item}
                      footer={
                        <>
                          {savedAd.notes && (
                            <div className="mx-4 mb-3 rounded-lg border border-stone-800 bg-stone-900/60 p-3">
                              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-400">
                                <StickyNote className="h-3.5 w-3.5" />
                                Tu nota
                              </div>
                              <p className="whitespace-pre-line text-sm text-stone-200">{savedAd.notes}</p>
                            </div>
                          )}

                          {(carpeta || savedAd.tags?.length > 0) && (
                            <div className="flex flex-wrap gap-1 px-4 pb-3">
                              {carpeta && (
                                <span className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-300">
                                  <Folder className="h-3 w-3" />
                                  {carpeta}
                                </span>
                              )}
                              {(savedAd.tags || []).map(tag => (
                                <span key={tag} className="rounded-full border border-stone-700 px-2 py-0.5 text-xs text-stone-400">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="mt-auto grid grid-cols-2 gap-2 border-t border-stone-800 p-3">
                            <button
                              onClick={() => handleToggleFavorite(savedAd._id, savedAd.isFavorite)}
                              className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm ${
                                savedAd.isFavorite
                                  ? 'bg-rose-900/30 text-rose-300 hover:bg-rose-900/50'
                                  : 'bg-stone-800 text-stone-200 hover:bg-stone-700'
                              }`}
                              title={savedAd.isFavorite ? 'Quitar de favoritos' : 'Marcar como favorito'}
                            >
                              <Heart className={`h-4 w-4 ${savedAd.isFavorite ? 'fill-current' : ''}`} />
                              {savedAd.isFavorite ? 'En favoritos' : 'Favorito'}
                            </button>
                            <button
                              onClick={() => handleMoveToFolder(savedAd._id, savedAd.folderId)}
                              className="flex items-center justify-center gap-1.5 rounded-lg bg-stone-800 px-3 py-2 text-sm text-stone-200 hover:bg-stone-700"
                              title="Pasar este anuncio a otra carpeta"
                            >
                              <Move className="h-4 w-4" />
                              Mover
                            </button>
                            {ad.ad_snapshot_url && (
                              <a
                                href={ad.ad_snapshot_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-1.5 rounded-lg bg-stone-800 px-3 py-2 text-sm text-stone-200 hover:bg-stone-700"
                                title="Abre el anuncio original en la biblioteca de anuncios de Meta"
                              >
                                <ExternalLink className="h-4 w-4" />
                                Ver en Meta
                              </a>
                            )}
                            <button
                              onClick={() => handleDelete(savedAd._id, ad.page_name)}
                              className="flex items-center justify-center gap-1.5 rounded-lg bg-stone-800 px-3 py-2 text-sm text-stone-300 hover:bg-rose-900/40 hover:text-rose-300"
                              title="Quitarlo de tus anuncios guardados"
                            >
                              <Trash2 className="h-4 w-4" />
                              Quitar
                            </button>
                          </div>
                        </>
                      }
                    />
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Move to Folder Modal */}
      <FolderSelectorModal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false)
          setAdToMove(null)
        }}
        onSelect={handleFolderSelect}
        title="Mover a carpeta"
        currentFolderId={adToMove?.currentFolderId}
      />
    </div>
  )
}